export const PERMISSIONS = {
  DASHBOARD: 'dashboard',
  
  PRODUCTS: {
    VIEW: 'view_products',
    CREATE: 'create_products',
    EDIT: 'edit_products',
    DELETE: 'delete_products',
    VIEW_DELETED: 'view_deleted_products',
    RESTORE_DELETED: 'restore_deleted_products'
  },

  PACKAGES: {
    VIEW: 'view_packages',
    CREATE: 'create_packages',
    EDIT: 'edit_packages',
    DELETE: 'delete_packages'
  },

  CATEGORIES: {
    VIEW: 'view_categories',
    CREATE: 'create_categories',
    EDIT: 'edit_categories',
    DELETE: 'delete_categories',
  },

  BOOKINGS: {
    VIEW: 'view_bookings',
    CREATE: 'create_bookings',
    EDIT: 'edit_bookings',
    DELETE: 'delete_bookings',
    DELIVERY: 'delivery_document',
    COLLECTION: 'collection_document',
    CALENDAR: 'view_calendar',
    VIEW_DELETED: 'view_deleted_bookings',
    RESTORE_DELETED: 'restore_deleted_bookings'
  },

  DISCOUNTS: {
    VIEW: 'view_discounts',
    CREATE: 'create_discounts',
    EDIT: 'edit_discounts',
    DELETE: 'delete_discounts'
  },

  CUSTOMERS: {
    VIEW: 'view_customers',
    DELETE: 'delete_customers',
    VIEW_BLACKLIST: 'view_blacklisted_customers',
    DELETE_BLACKLIST: 'delete_blacklisted_customers',
    VIEW_INQUIRIES: 'view_customer_inquiries',
    UPDATE_INQUIRIES: 'update_customer_inquiries',
    VIEW_CALLBACKS: 'view_customer_callbacks',
    UPDATE_CALLBACKS: 'update_customer_callbacks', 
    VIEW_NEWSLETTER: 'view_newsletter_subscriptions',
    UPDATE_NEWSLETTER: 'update_newsletter_subscriptions'
  },

  USERS: {
    VIEW: 'view_users',
    CREATE: 'create_users',
    EDIT: 'edit_users',
    DELETE: 'delete_users'
  },

  SETTINGS: {
    GENERAL: 'manage_settings',
    DELIVERY: 'delivery_settings'
  },

  HOLIDAYS: {
    VIEW: 'view_holidays',
    CREATE: 'create_holidays',
    EDIT: 'edit_holidays',
    DELETE: 'delete_holidays'
  },

  BOOKING_QUESTIONS: {
    VIEW: 'view_booking_questions',
    CREATE: 'create_booking_questions',
    EDIT: 'edit_booking_questions',
    DELETE: 'delete_booking_questions'
  },

  AUTOMATION: {
    SMS_TEMPLATES: 'manage_sms_templates',
    EMAIL_TEMPLATES: 'manage_email_templates'
  },

  WEB_PAGES: {
    MANAGE: 'manage_web_pages'
  }
} as const;

// Get all permission string literals
export type PermissionValues = {
  [K in keyof typeof PERMISSIONS]: typeof PERMISSIONS[K] extends string 
    ? typeof PERMISSIONS[K] 
    : typeof PERMISSIONS[K][keyof typeof PERMISSIONS[K]]
}[keyof typeof PERMISSIONS];

// Type for all permission values
export type Permission = PermissionValues;

export type Role = 'Admin' | 'Manager';

export const ROLE_PERMISSIONS: Record<Role, PermissionValues[]> = {
  Admin: [
    PERMISSIONS.DASHBOARD,
    // Products
    PERMISSIONS.PRODUCTS.VIEW,
    PERMISSIONS.PRODUCTS.CREATE,
    PERMISSIONS.PRODUCTS.EDIT,
    PERMISSIONS.PRODUCTS.DELETE,
    PERMISSIONS.PRODUCTS.VIEW_DELETED,
    PERMISSIONS.PRODUCTS.RESTORE_DELETED,
    // Packages
    PERMISSIONS.PACKAGES.VIEW,
    PERMISSIONS.PACKAGES.CREATE,
    PERMISSIONS.PACKAGES.DELETE,
    PERMISSIONS.PACKAGES.EDIT,
    // Categories
    PERMISSIONS.CATEGORIES.VIEW,
    PERMISSIONS.CATEGORIES.CREATE,
    PERMISSIONS.CATEGORIES.EDIT,
    PERMISSIONS.CATEGORIES.DELETE,
    // Bookings
    PERMISSIONS.BOOKINGS.VIEW,
    PERMISSIONS.BOOKINGS.CREATE,
    PERMISSIONS.BOOKINGS.EDIT,
    PERMISSIONS.BOOKINGS.DELETE,
    PERMISSIONS.BOOKINGS.DELIVERY,
    PERMISSIONS.BOOKINGS.COLLECTION,
    PERMISSIONS.BOOKINGS.CALENDAR,
    PERMISSIONS.BOOKINGS.VIEW_DELETED,
    PERMISSIONS.BOOKINGS.RESTORE_DELETED,
    // Discounts
    PERMISSIONS.DISCOUNTS.VIEW,
    PERMISSIONS.DISCOUNTS.CREATE,
    PERMISSIONS.DISCOUNTS.EDIT,
    PERMISSIONS.DISCOUNTS.DELETE,
    // Customers
    PERMISSIONS.CUSTOMERS.VIEW,
    PERMISSIONS.CUSTOMERS.DELETE,
    PERMISSIONS.CUSTOMERS.VIEW_BLACKLIST,
    PERMISSIONS.CUSTOMERS.DELETE_BLACKLIST,
    PERMISSIONS.CUSTOMERS.VIEW_INQUIRIES,
    PERMISSIONS.CUSTOMERS.UPDATE_INQUIRIES,
    PERMISSIONS.CUSTOMERS.VIEW_CALLBACKS,
    PERMISSIONS.CUSTOMERS.UPDATE_CALLBACKS,
    PERMISSIONS.CUSTOMERS.VIEW_NEWSLETTER,
    PERMISSIONS.CUSTOMERS.UPDATE_NEWSLETTER,
    // Users
    PERMISSIONS.USERS.VIEW,
    PERMISSIONS.USERS.CREATE,
    PERMISSIONS.USERS.EDIT,
    PERMISSIONS.USERS.DELETE,
    // Settings
    PERMISSIONS.SETTINGS.GENERAL,
    PERMISSIONS.SETTINGS.DELIVERY,
    // Holidays
    PERMISSIONS.HOLIDAYS.VIEW,
    PERMISSIONS.HOLIDAYS.CREATE,
    PERMISSIONS.HOLIDAYS.EDIT,
    PERMISSIONS.HOLIDAYS.DELETE,
    // Booking Questions
    PERMISSIONS.BOOKING_QUESTIONS.VIEW,
    PERMISSIONS.BOOKING_QUESTIONS.CREATE,
    PERMISSIONS.BOOKING_QUESTIONS.EDIT,
    PERMISSIONS.BOOKING_QUESTIONS.DELETE,
    // Automation
    PERMISSIONS.AUTOMATION.SMS_TEMPLATES,
    PERMISSIONS.AUTOMATION.EMAIL_TEMPLATES,
    // Web Pages
    PERMISSIONS.WEB_PAGES.MANAGE
  ],
  Manager: [
    PERMISSIONS.DASHBOARD,
    // Products - All permissions
    PERMISSIONS.PRODUCTS.VIEW,
    PERMISSIONS.PRODUCTS.CREATE,
    PERMISSIONS.PRODUCTS.EDIT,
    PERMISSIONS.PRODUCTS.DELETE,
    PERMISSIONS.PRODUCTS.VIEW_DELETED,
    PERMISSIONS.PRODUCTS.RESTORE_DELETED,
    // Bookings - All permissions
    PERMISSIONS.BOOKINGS.VIEW,
    PERMISSIONS.BOOKINGS.CREATE,
    PERMISSIONS.BOOKINGS.EDIT,
    PERMISSIONS.BOOKINGS.DELETE,
    PERMISSIONS.BOOKINGS.DELIVERY,
    PERMISSIONS.BOOKINGS.COLLECTION,
    PERMISSIONS.BOOKINGS.CALENDAR,
    PERMISSIONS.BOOKINGS.VIEW_DELETED,
    PERMISSIONS.BOOKINGS.RESTORE_DELETED
  ]
};

// Helper function to check if a user has permission
export const hasPermission = (role: Role, permission: PermissionValues | PermissionValues[]): boolean => {
  if (!ROLE_PERMISSIONS[role]) return false;
  
  if (Array.isArray(permission)) {
    return permission.every(p => ROLE_PERMISSIONS[role].includes(p));
  }
  
  return ROLE_PERMISSIONS[role].includes(permission);
};

// Helper function to get all permissions for a role
export const getRolePermissions = (role: Role): PermissionValues[] => {
  return ROLE_PERMISSIONS[role] || [];
};


